import { defineStore } from 'pinia'
import { getConfig, saveConfig } from 'src/service/config.user'
import { computed, ref, watch } from 'vue'
import { useAuthStore } from 'src/common/store'

export const useUserConfigStore = defineStore('user.config', () => {
  const hash = ref<string>()
  const userConfig = ref<Dictionary<any>>({})
  const lastUpdated = ref<Date>()
  const refreshInterval = ref<number>()
  const isInitialized = ref(false)
  let pendingRefresh: Promise<void> | null = null

  const authStore = useAuthStore()

  // Watch for auth and tenant changes
  watch(
    () => [
      authStore.isAuthenticated,
      authStore.user,
      authStore.currentOrgId,
      authStore.currentCenterId,
    ],
    async ([isAuthenticated, user, orgId, centerId]) => {
      const hasRequiredContext = !!(user && orgId && centerId)

      if (isAuthenticated && hasRequiredContext && !isInitialized.value) {
        // User logged in with context, initialize the store
        await initialize()
      } else if (isAuthenticated && hasRequiredContext && isInitialized.value) {
        // Context changed, refresh config
        await refresh()
      } else if (!isAuthenticated && isInitialized.value) {
        // User logged out, clean up
        stopPeriodicRefresh()
        isInitialized.value = false
        hash.value = undefined
        userConfig.value = {}
        lastUpdated.value = undefined
      }
    },
    { immediate: true },
  )

  /**
   * Initialize the store and start periodic refresh
   * Called once when user logs in with org/center context
   */
  async function initialize() {
    if (isInitialized.value) return
    if (!canRefresh()) return // Only initialize if authenticated with context

    // Initial load
    await refresh()
    isInitialized.value = true

    // Start periodic refresh every 5 minutes
    startPeriodicRefresh()
  }

  /**
   * Check if we can make API calls
   */
  function canRefresh(): boolean {
    return !!(
      authStore.isAuthenticated &&
      authStore.currentOrgId &&
      authStore.currentCenterId
    )
  }

  /**
   * Start periodic refresh interval
   */
  function startPeriodicRefresh() {
    if (refreshInterval.value) return

    refreshInterval.value = setInterval(
      () => {
        void refresh()
      },
      5 * 60 * 1000,
    ) as unknown as number
  }

  /**
   * Stop periodic refresh interval
   */
  function stopPeriodicRefresh() {
    if (refreshInterval.value) {
      clearInterval(refreshInterval.value)
      refreshInterval.value = undefined
    }
  }

  const pinnedSpace = computed<string[]>(
    () => userConfig.value['pinnedSpace'] || [],
  )
  const pinnedStaff = computed<string[]>(
    () => userConfig.value['pinnedStaff'] || [],
  )

  function get<T>(key: string, defaultValue?: T) {
    if (userConfig.value[key] !== undefined) return userConfig.value[key] as T
    return defaultValue
  }

  async function set<T>(key: string, value: T) {
    userConfig.value[key] = value
    const response = await saveConfig(userConfig.value)
    hash.value = response.hash
    userConfig.value = response.config
    lastUpdated.value = new Date()
  }

  async function setMany(entries: Record<string, any>) {
    Object.assign(userConfig.value, entries)
    const response = await saveConfig(userConfig.value)
    hash.value = response.hash
    userConfig.value = response.config
    lastUpdated.value = new Date()
  }

  async function remove(key: string) {
    delete userConfig.value[key]
    const response = await saveConfig(userConfig.value)
    hash.value = response.hash
    userConfig.value = response.config
    lastUpdated.value = new Date()
  }

  async function refresh() {
    // Only refresh if user is authenticated with context
    if (!canRefresh()) return

    // Dedupe concurrent refresh calls (e.g. the auth watcher and an explicit
    // caller both firing at boot) into a single in-flight request
    if (pendingRefresh) return pendingRefresh

    pendingRefresh = (async () => {
      try {
        const response = await getConfig()
        hash.value = response.hash
        userConfig.value = response.config
        lastUpdated.value = new Date()
      } catch (error) {
        // Silently fail - config refresh is not critical
        if (process.env.DEV) {
          console.error('[UserConfigStore] Failed to refresh config:', error)
        }
      } finally {
        pendingRefresh = null
      }
    })()

    return pendingRefresh
  }

  return {
    pinnedSpace,
    pinnedStaff,
    get,
    set,
    setMany,
    remove,
    refresh,
    initialize,
    stopPeriodicRefresh,
    isInitialized,
  }
})
